import React from 'react';

import DropdownButton from '../../common2/DropdownButton/DropdownButton';
import DropdownMenuItem from '../../common/DropdownMenuItem/DropdownMenuItem';
import EditIcon from '../../../icons/more-vertical.svg';
import ViewIcon from '../../../icons/view.svg';
import WarningIcon from '../../../icons/warning.svg';
import RemoveIcon from '../../../icons/remove.svg';
import ArrowRightIcon from '../../../icons/arrow-right.svg';
import { ListClaim } from '../../../api/types';
import { CustomerActionType, getActionTypeForClaim } from '../take-action/actionHelper';
import { ClaimStatus } from '../../parcel-finder/claims/types';

import styles from './ClaimsTableActionDropdownButton.scss';

type CustomerActionTypeProps = {
  icon: React.ComponentType;
  intent: 'primary' | 'action' | 'cancel';
  name: string;
  children: React.ReactNode;
};

const actionButtonPropsByType: Record<CustomerActionType, CustomerActionTypeProps | null> = {
  [CustomerActionType.NONE]: null,
  [CustomerActionType.TAKE_CUSTOMER_ACTION]: {
    icon: WarningIcon,
    intent: 'action',
    name: 'takeActionButton',
    children: 'Take action',
  },
  [CustomerActionType.VIEW_DECLINED_REASON]: {
    icon: ViewIcon,
    name: 'viewDeclinedReasonButton',
    intent: 'primary',
    children: 'View reason',
  },
  [CustomerActionType.DISPUTE_DECLINED_REASON]: {
    icon: ViewIcon,
    name: 'disputeDeclinedReasonButton',
    intent: 'primary',
    children: 'View reason',
  },
};

type Props = {
  claim?: ListClaim;
  onTakeActionButtonClick: (claim: ListClaim) => void;
  onCancelClaimButtonClick: (claim: ListClaim) => void;
  onContinueClaimSubmission: (claim: ListClaim) => void;
};

function ClaimsTableActionDropdownButton(props: Props) {
  const { claim, onTakeActionButtonClick, onCancelClaimButtonClick, onContinueClaimSubmission } = props;

  if (!claim) {
    return null;
  }

  const customerActionType = getActionTypeForClaim(claim);
  const customerActionProps = actionButtonPropsByType[customerActionType];

  const menuItems = [
    claim.status === ClaimStatus.DRAFT && (
      <DropdownMenuItem
        intent="primary"
        name="continueSubmissionMenuItem"
        icon={ArrowRightIcon}
        onClick={() => onContinueClaimSubmission(claim)}
      >
        Continue submission
      </DropdownMenuItem>
    ),
    claim.shipmentId && (
      <a className={styles.link} href={`/parcel-finder/shipments/${claim.shipmentId}`} target="_blank" rel="noopener">
        <DropdownMenuItem intent="primary" name="viewShipmentDetailButton" icon={ViewIcon}>
          View details
        </DropdownMenuItem>
      </a>
    ),
    claim.isCancellableByCustomer && (
      <DropdownMenuItem
        intent="danger"
        name="cancelClaimButton"
        icon={RemoveIcon}
        onClick={() => onCancelClaimButtonClick(claim)}
      >
        Cancel claim
      </DropdownMenuItem>
    ),
    customerActionProps && (
      <DropdownMenuItem
        intent={customerActionProps.intent}
        name={customerActionProps.name}
        icon={customerActionProps.icon}
        onClick={() => onTakeActionButtonClick(claim)}
      >
        {customerActionProps.children}
      </DropdownMenuItem>
    ),
  ].filter(Boolean);

  if (menuItems.length === 0) {
    return null;
  }

  return (
    <DropdownButton ariaLabel="actions-dropdown-button" icon={EditIcon} tooltipText="Actions">
      {menuItems}
    </DropdownButton>
  );
}

export default React.memo(ClaimsTableActionDropdownButton);
